// src/shared/utils/logoutCleanup.ts
/**
 * Centralized logout cleanup utility
 * Clears all cache, localStorage, sessionStorage, and resets all stores
 */

import { queryClient } from '../../core/providers/queryClient';
import { useUserStore } from '../../app/store/useUserStore';
import { useNotificationStore } from '../../app/store/useNotificationStore';
import { useChatStore } from '../../app/store/useChatStore';
import { useReceiverStore } from '../../app/store/useReceiverStore';
import { useErrorReportStore } from '../../app/store/useErrorReportStore';
import { useItineraryStore } from '../../app/store/useItineraryStore';
import { useShipmentStore } from '../../app/store/useShipmentStore';
import { useToastStore } from '../../app/store/useToastStore';

export const GLOBAL_LOGOUT_KEY = 'skyporter_global_logout';

type Auth0LogoutFn = (options?: { logoutParams?: { returnTo?: string; federated?: boolean } }) => void;
let _auth0Logout: Auth0LogoutFn | null = null;

export const registerAuth0Logout = (fn: Auth0LogoutFn) => {
  _auth0Logout = fn;
};

/**
 * Performs complete cleanup of all application state on logout
 */
export const performLogoutCleanup = () => {
  try {
    // 1. Clear React Query cache
    queryClient.clear();

    // 2. Reset all Zustand stores FIRST
    // This prevents stores from re-writing to localStorage after we clear it
    useUserStore.getState().logout();
    useNotificationStore.getState().clearNotificationData();
    useChatStore.setState({ unreadCounts: {} });
    useReceiverStore.getState().clearReceiverData();
    useErrorReportStore.getState().clearCorrelationId();
    useItineraryStore.getState().resetForm();
    useShipmentStore.getState().resetForm();
    useToastStore.getState().clearAllToasts();

    // 3. Clear localStorage selectively (preserve notifications so they persist across logins)
    // Get all keys before clearing to handle dynamic keys (like OTP counters)
    const localStorageKeys = Object.keys(localStorage);

    // Keys to preserve across logout (notifications are filtered by userId so they're safe to keep)
    const keysToPreserve = [
      'skyporter_notifications',
    ];

    // Keys to explicitly remove
    const keysToRemove = [
      // Zustand persisted stores
      'skyporter-user-store',
      'chat-storage',
      'error-report-storage',
      'receiver-storage',

      // Application data
      'skyporter_currencies',
      'user_coordinates',
      'exchangeRates',
      'exchangeRatesTimestamp',
      'pending_order_code',
      'pending_notification_id',
      'pending_offer_order_code',
      'pending_backend_notification_id',
      'auth_cancelled',

      // Dynamic OTP resend counters (pattern: otp_resend_count_*)
      ...localStorageKeys.filter(key => key.startsWith('otp_resend_count_')),
    ];

    // Remove all localStorage keys except preserved ones
    localStorageKeys.forEach(key => {
      if (!keysToPreserve.includes(key)) {
        try {
          localStorage.removeItem(key);
        } catch {
        }
      }
    });

    // Also explicitly remove the listed keys (in case they were re-added by persist middleware)
    keysToRemove.forEach(key => {
      try {
        localStorage.removeItem(key);
      } catch {
      }
    });

    // Clear all sessionStorage
    sessionStorage.clear();
  } catch {
    // Continue with logout even if cleanup fails partially
  }
};

/**
 * Broadcasts a logout signal to all other open tabs via localStorage event.
 * Other tabs listen for the `storage` event on GLOBAL_LOGOUT_KEY and trigger their own logout.
 */
export const broadcastLogout = () => {
  try {
    localStorage.setItem(GLOBAL_LOGOUT_KEY, Date.now().toString());
    // Remove immediately so the key doesn't persist after logout clears storage
    localStorage.removeItem(GLOBAL_LOGOUT_KEY);
  } catch {
    // localStorage unavailable — silently ignore
  }
};

/**
 * Full logout: cleans up local state, broadcasts to other tabs, then redirects
 * through Auth0's logout endpoint so the Auth0 session is fully terminated.
 */
export const performFullLogout = () => {
  performLogoutCleanup();
  broadcastLogout();
  if (_auth0Logout) {
    _auth0Logout({ logoutParams: { returnTo: window.location.origin, federated: true } });
  } else {
    // Fallback if Auth0 logout was never registered (shouldn't happen in normal flow)
    window.location.href = window.location.origin;
  }
};
